/**
 * A codebook from a plain table of variables, and the SPSS and R syntax that applies it.
 *
 * One line per variable, fields separated by a tab (a spreadsheet paste) or a pipe:
 *
 *     name | label | type | value labels | missing codes | notes
 *
 * - **type** is scale, ordinal, nominal or string (a few synonyms are read too).
 * - **value labels** are `code=label` pairs separated by semicolons: `1=Female; 2=Male`.
 * - **missing codes** are comma-separated: `-99, -98`, or a range `997 to 999`; `LO` and
 *   `HI` stand for open ends.
 *
 * The SPSS commands are VARIABLE LABELS, VALUE LABELS, MISSING VALUES and VARIABLE LEVEL,
 * with each value label on its own line (a syntax line is limited to 251 characters). The
 * rules it checks against come from IBM's command syntax reference: variable names of up to
 * 64 bytes starting with a letter or @ (a leading # makes a scratch variable and a leading $
 * is reserved for system variables), not ending in a period, and not one of ALL,
 * AND, BY, EQ, GE, GT, LE, LT, NE, NOT, OR, TO, WITH; labels up to 256 bytes and value labels
 * up to 120; at most three discrete user-missing values, or one range plus one discrete
 * value for a numeric variable; and quoted values for strings.
 */

export type VarType = "scale" | "ordinal" | "nominal" | "string";

export interface ValueLabel {
  code: string;
  label: string;
}

export interface MissingRange {
  /** A number as text, or "LO". */
  low: string;
  /** A number as text, or "HI". */
  high: string;
}

export interface Variable {
  name: string;
  label: string;
  type: VarType;
  values: ValueLabel[];
  missing: { discrete: string[]; range: MissingRange | null };
  notes: string;
  line: number;
}

export type IssueKind =
  | "no-name"
  | "type"
  | "value-pair"
  | "code-not-number"
  | "duplicate-code"
  | "missing-token"
  | "missing-string-range"
  | "name-duplicate"
  | "spss-name"
  | "spss-missing-limit"
  | "spss-label-length"
  | "spss-value-label-length"
  | "spss-line-length"
  | "r-name";

export interface Issue {
  line: number;
  kind: IssueKind;
  /** The name, token or text the issue is about. */
  detail: string;
}

/* ------------------------------------------------------------------ reading */

const LINE_BREAK = /\r\n|\r|\n/;
const NUMBER = /^[-+]?(?:\d+\.?\d*|\.\d+)$/;
const RANGE = /^(\S+)\s+(?:to|thru|through)\s+(\S+)$/i;
const HEADER_CELL = /^(?:name|variable|var|variable name)$/i;
const SURROUNDING_MARKS = /^[\s"'`*_|:]+|[\s"'`*_|:]+$/g;

const TYPE_WORDS: Record<string, VarType> = {
  scale: "scale",
  numeric: "scale",
  number: "scale",
  continuous: "scale",
  interval: "scale",
  ratio: "scale",
  ordinal: "ordinal",
  ordered: "ordinal",
  nominal: "nominal",
  categorical: "nominal",
  factor: "nominal",
  string: "string",
  text: "string",
  character: "string",
  str: "string",
};

function splitFields(line: string, delimiter: string): string[] {
  return line.split(delimiter).map((cell) => cell.trim());
}

function normalizeNumber(token: string): string {
  return String(Number(token));
}

function parseValueLabels(
  text: string,
  type: VarType,
  line: number,
  issues: Issue[]
): ValueLabel[] {
  const labels: ValueLabel[] = [];
  const seen = new Set<string>();
  for (const pair of text.split(";")) {
    const trimmed = pair.trim();
    if (trimmed === "") {
      continue;
    }
    const at = trimmed.indexOf("=");
    if (at <= 0) {
      issues.push({ line, kind: "value-pair", detail: trimmed });
      continue;
    }
    const rawCode = trimmed.slice(0, at).trim();
    const label = trimmed.slice(at + 1).trim();
    if (type !== "string" && !NUMBER.test(rawCode)) {
      issues.push({ line, kind: "code-not-number", detail: rawCode });
      continue;
    }
    const code = type === "string" ? rawCode : normalizeNumber(rawCode);
    if (seen.has(code)) {
      issues.push({ line, kind: "duplicate-code", detail: code });
      continue;
    }
    seen.add(code);
    labels.push({ code, label });
  }
  return labels;
}

function boundOf(token: string, open: "LO" | "HI"): string | null {
  if (
    token.toUpperCase() === open ||
    token.toUpperCase() === (open === "LO" ? "LOWEST" : "HIGHEST")
  ) {
    return open;
  }
  return NUMBER.test(token) ? normalizeNumber(token) : null;
}

function parseMissing(
  text: string,
  type: VarType,
  line: number,
  issues: Issue[]
): Variable["missing"] {
  const missing: Variable["missing"] = { discrete: [], range: null };
  for (const part of text.split(",")) {
    const token = part.trim();
    if (token === "") {
      continue;
    }
    const range = RANGE.exec(token);
    if (range) {
      const low = boundOf(range[1], "LO");
      const high = boundOf(range[2], "HI");
      if (type === "string") {
        issues.push({ line, kind: "missing-string-range", detail: token });
      } else if (low === null || high === null || missing.range) {
        issues.push({ line, kind: "missing-token", detail: token });
      } else {
        missing.range = { low, high };
      }
    } else if (type === "string") {
      missing.discrete.push(token);
    } else if (NUMBER.test(token)) {
      missing.discrete.push(normalizeNumber(token));
    } else {
      issues.push({ line, kind: "missing-token", detail: token });
    }
  }
  return missing;
}

/** Words a translated page uses, on top of the English ones that are always read. */
export interface CodebookWords {
  /** Extra first-row headings, e.g. the page's translated name-column label. */
  headerWords?: readonly string[];
  /** The page's own labels for each type; read as synonyms for the English words. */
  typeWords?: Partial<Record<VarType, string>>;
}

function plainWord(text: string): string {
  return text.replace(SURROUNDING_MARKS, "").toLowerCase();
}

/**
 * Whether the first cell of the first row is a column heading rather than a variable.
 * English headings are always recognised; `headerWords` adds the page's translated
 * ones, so "Nom" or "名前" is skipped as well. Matching ignores case and any Markdown
 * or quote decoration around the word.
 */
function isHeaderCell(name: string, headerWords: readonly string[]): boolean {
  if (HEADER_CELL.test(name)) {
    return true;
  }
  const word = plainWord(name);
  return word !== "" && headerWords.some((extra) => plainWord(extra) === word);
}

const TYPE_ORDER: readonly VarType[] = [
  "scale",
  "ordinal",
  "nominal",
  "string",
];

/** The English synonyms plus the page's own type labels, keyed by lower-case word. */
function typeLookup(
  typeWords: CodebookWords["typeWords"]
): Record<string, VarType> {
  if (!typeWords) {
    return TYPE_WORDS;
  }
  const lookup: Record<string, VarType> = { ...TYPE_WORDS };
  for (const type of TYPE_ORDER) {
    const label = typeWords[type];
    if (label && plainWord(label) !== "") {
      lookup[plainWord(label)] = type;
    }
  }
  return lookup;
}

function typeFrom(
  raw: string,
  hasValues: boolean,
  line: number,
  issues: Issue[],
  lookup: Record<string, VarType> = TYPE_WORDS
): VarType {
  if (raw === "") {
    return hasValues ? "nominal" : "scale";
  }
  const key = plainWord(raw);
  const found = Object.hasOwn(lookup, key) ? lookup[key] : undefined;
  if (found) {
    return found;
  }
  issues.push({ line, kind: "type", detail: raw });
  return hasValues ? "nominal" : "scale";
}

export interface ParsedCodebook {
  variables: Variable[];
  issues: Issue[];
}

export function parseCodebook(
  text: string,
  words: CodebookWords = {}
): ParsedCodebook {
  const headerWords = words.headerWords ?? [];
  const lookup = typeLookup(words.typeWords);
  const variables: Variable[] = [];
  const issues: Issue[] = [];
  const lines = text.split(LINE_BREAK);
  const delimiter = lines.some((line) => line.includes("\t")) ? "\t" : "|";
  lines.forEach((raw, index) => {
    if (raw.trim() === "") {
      return;
    }
    const line = index + 1;
    const [
      name = "",
      label = "",
      typeText = "",
      valueText = "",
      missingText = "",
      notes = "",
    ] = splitFields(raw, delimiter);
    if (variables.length === 0 && isHeaderCell(name, headerWords)) {
      return;
    }
    if (name === "") {
      issues.push({ line, kind: "no-name", detail: raw.trim() });
      return;
    }
    const type = typeFrom(
      typeText,
      valueText.trim() !== "",
      line,
      issues,
      lookup
    );
    variables.push({
      name,
      label,
      type,
      values: parseValueLabels(valueText, type, line, issues),
      missing: parseMissing(missingText, type, line, issues),
      notes,
      line,
    });
  });
  return { variables, issues: [...issues, ...checkVariables(variables)] };
}

/* ------------------------------------------------------------------ checks */

const SPSS_RESERVED = new Set([
  "ALL",
  "AND",
  "BY",
  "EQ",
  "GE",
  "GT",
  "LE",
  "LT",
  "NE",
  "NOT",
  "OR",
  "TO",
  "WITH",
]);
const SPSS_NAME = /^[\p{L}@][\p{L}\p{N}._@#$]*$/u;
const R_RESERVED = new Set([
  "if",
  "else",
  "repeat",
  "while",
  "function",
  "for",
  "next",
  "break",
  "TRUE",
  "FALSE",
  "NULL",
  "Inf",
  "NaN",
  "NA",
  "in",
]);
const R_NAME = /^(?:[\p{L}]|\.(?![0-9]))[\p{L}\p{N}._]*$/u;
/** IBM says 256; SPSS has been seen warning at 251, so the tool keeps to the lower figure. */
const SPSS_LINE_LIMIT = 251;

export function spssNameProblem(name: string): boolean {
  return (
    !SPSS_NAME.test(name) ||
    name.endsWith(".") ||
    new TextEncoder().encode(name).length > 64 ||
    SPSS_RESERVED.has(name.toUpperCase())
  );
}

export function rNameProblem(name: string): boolean {
  return !R_NAME.test(name) || R_RESERVED.has(name);
}

function bytes(text: string): number {
  return new TextEncoder().encode(text).length;
}

/** The longest SPSS syntax line, in bytes, any of this variable's commands would need. */
function longestSpssLine(variable: Variable): number {
  const candidates = [
    `  /${variable.name} ${quoteSpss(variable.label)}`,
    ...variable.values.map(
      (item) =>
        `    ${spssCode(item.code, variable.type)} ${quoteSpss(item.label)}`
    ),
  ];
  return Math.max(...candidates.map((text) => bytes(text)));
}

function checkOne(variable: Variable, duplicate: boolean): Issue[] {
  const { name, line } = variable;
  const at = (kind: IssueKind): Issue => ({ line, kind, detail: name });
  const { discrete, range } = variable.missing;
  const checks: [boolean, IssueKind][] = [
    [duplicate, "name-duplicate"],
    [spssNameProblem(name), "spss-name"],
    [rNameProblem(name), "r-name"],
    [bytes(variable.label) > 256, "spss-label-length"],
    [
      variable.values.some((item) => bytes(item.label) > 120),
      "spss-value-label-length",
    ],
    [discrete.length > (range ? 1 : 3), "spss-missing-limit"],
    [longestSpssLine(variable) > SPSS_LINE_LIMIT, "spss-line-length"],
  ];
  return checks.filter(([failed]) => failed).map(([, kind]) => at(kind));
}

function checkVariables(variables: readonly Variable[]): Issue[] {
  const names = new Map<string, number>();
  for (const variable of variables) {
    const key = variable.name.toLowerCase();
    names.set(key, (names.get(key) ?? 0) + 1);
  }
  return variables.flatMap((variable) =>
    checkOne(variable, (names.get(variable.name.toLowerCase()) ?? 0) > 1)
  );
}

/* ------------------------------------------------------------------ SPSS */

/** A string in SPSS syntax: double quotes, with an embedded double quote doubled. */
export function quoteSpss(text: string): string {
  return `"${text.replaceAll('"', '""')}"`;
}

function spssCode(code: string, type: VarType): string {
  return type === "string" ? quoteSpss(code) : code;
}

function spssMissing(variable: Variable): string | null {
  const { discrete, range } = variable.missing;
  const parts: string[] = [];
  if (range) {
    parts.push(`${range.low} THRU ${range.high}`);
  }
  parts.push(
    ...discrete.map((value) =>
      variable.type === "string" ? quoteSpss(value) : value
    )
  );
  return parts.length > 0 ? parts.join(", ") : null;
}

const SPSS_LEVEL: Record<VarType, string | null> = {
  scale: "SCALE",
  ordinal: "ORDINAL",
  nominal: "NOMINAL",
  string: null,
};

/** One SPSS command: a keyword, then one block per variable joined by slashes. */
function command(keyword: string, blocks: readonly string[]): string | null {
  if (blocks.length === 0) {
    return null;
  }
  const body = blocks
    .map((block, index) => (index === 0 ? `  ${block}` : `  /${block}`))
    .join("\n");
  return `${keyword}\n${body}.`;
}

export function spssSyntax(variables: readonly Variable[]): string {
  const labelled = variables.filter((variable) => variable.label !== "");
  const valued = variables.filter((variable) => variable.values.length > 0);
  const withMissing = variables.filter(
    (variable) => spssMissing(variable) !== null
  );
  const leveled = variables.filter(
    (variable) => SPSS_LEVEL[variable.type] !== null
  );
  const commands = [
    "* Codebook syntax. Run it with your data file open.",
    command(
      "VARIABLE LABELS",
      labelled.map(
        (variable) => `${variable.name} ${quoteSpss(variable.label)}`
      )
    ),
    command(
      "VALUE LABELS",
      valued.map(
        (variable) =>
          `${variable.name}\n${variable.values
            .map(
              (item) =>
                `    ${spssCode(item.code, variable.type)} ${quoteSpss(item.label)}`
            )
            .join("\n")}`
      )
    ),
    command(
      "MISSING VALUES",
      withMissing.map(
        (variable) => `${variable.name} (${spssMissing(variable)})`
      )
    ),
    command(
      "VARIABLE LEVEL",
      leveled.map(
        (variable) => `${variable.name} (${SPSS_LEVEL[variable.type]})`
      )
    ),
  ];
  return commands.filter((text): text is string => text !== null).join("\n\n");
}

/* ------------------------------------------------------------------ R */

export type RStyle = "base" | "haven";

/** A string in R: double quotes, with backslashes and double quotes escaped. */
export function quoteR(text: string): string {
  return `"${text.replaceAll("\\", "\\\\").replaceAll('"', '\\"')}"`;
}

function rValue(code: string, type: VarType): string {
  return type === "string" ? quoteR(code) : code;
}

function rVector(items: readonly string[]): string {
  return `c(${items.join(", ")})`;
}

function rAccess(frame: string, name: string): string {
  return rNameProblem(name)
    ? `${frame}[[${quoteR(name)}]]`
    : `${frame}$${name}`;
}

function rBound(value: string, open: "LO" | "HI"): string | null {
  if (value === open) {
    return null;
  }
  return value;
}

/** An R condition selecting the user-missing values of `target`, or null if none. */
function rMissingTest(variable: Variable, target: string): string | null {
  const { discrete, range } = variable.missing;
  const tests: string[] = [];
  if (discrete.length > 0) {
    const values = discrete.map((value) => rValue(value, variable.type));
    tests.push(`${target} %in% ${rVector(values)}`);
  }
  if (range) {
    const low = rBound(range.low, "LO");
    const high = rBound(range.high, "HI");
    const bounds = [
      ...(low === null ? [] : [`${target} >= ${low}`]),
      ...(high === null ? [] : [`${target} <= ${high}`]),
    ];
    tests.push(
      `(${(bounds.length > 0 ? bounds : [`!is.na(${target})`]).join(" & ")})`
    );
  }
  return tests.length > 0 ? tests.join(" | ") : null;
}

function rMissingLines(
  variables: readonly Variable[],
  frame: string
): string[] {
  return variables.flatMap((variable) => {
    const target = rAccess(frame, variable.name);
    const test = rMissingTest(variable, target);
    return test === null ? [] : [`${target}[which(${test})] <- NA`];
  });
}

function rBaseFactors(variables: readonly Variable[], frame: string): string[] {
  return variables.flatMap((variable) => {
    if (variable.type === "scale" || variable.type === "string") {
      return [];
    }
    const target = rAccess(frame, variable.name);
    const ordered = variable.type === "ordinal" ? ", ordered = TRUE" : "";
    const discrete = new Set(variable.missing.discrete);
    const kept = variable.values.filter((item) => !discrete.has(item.code));
    if (kept.length === 0) {
      return variable.type === "ordinal"
        ? [`${target} <- factor(${target}, ordered = TRUE)`]
        : [];
    }
    const levels = kept.map((item) => item.code);
    const labels = kept.map((item) => quoteR(item.label));
    return [
      `${target} <- factor(${target}, levels = c(${levels.join(", ")}), labels = c(${labels.join(", ")})${ordered})`,
    ];
  });
}

const R_COERCE: Record<VarType, string | null> = {
  scale: "as.numeric",
  ordinal: "as.numeric",
  nominal: "as.numeric",
  string: "as.character",
};

function rTypeLines(variables: readonly Variable[], frame: string): string[] {
  return variables.flatMap((variable) => {
    const coerce = R_COERCE[variable.type];
    const target = rAccess(frame, variable.name);
    // Categorical variables with string codes stay as they are.
    const stringCodes = variable.values.some((item) => !NUMBER.test(item.code));
    return coerce === null || (variable.type === "nominal" && stringCodes)
      ? []
      : [`${target} <- ${coerce}(${target})`];
  });
}

function rBaseSyntax(variables: readonly Variable[], frame: string): string {
  const labelLines = variables
    .filter((variable) => variable.label !== "")
    .map(
      (variable) =>
        `attr(${rAccess(frame, variable.name)}, "label") <- ${quoteR(variable.label)}`
    );
  const sections: [string, string[]][] = [
    ["1. Types", rTypeLines(variables, frame)],
    ["2. User-missing codes become NA", rMissingLines(variables, frame)],
    [
      "3. Value labels become factors (set after the codes above)",
      rBaseFactors(variables, frame),
    ],
    ["4. Variable labels (set last: factor() drops attributes)", labelLines],
  ];
  const body = sections
    .filter(([, lines]) => lines.length > 0)
    .map(([title, lines]) => `# ${title}\n${lines.join("\n")}`)
    .join("\n\n");
  return `# Codebook syntax for R (base R). Run it after reading your data into \`${frame}\`.\n\n${body}`;
}

function rHavenLine(variable: Variable, frame: string): string {
  const target = rAccess(frame, variable.name);
  const args: string[] = [target];
  if (variable.values.length > 0) {
    const pairs = variable.values.map(
      (item) => `${quoteR(item.label)} = ${rValue(item.code, variable.type)}`
    );
    args.push(`labels = c(${pairs.join(", ")})`);
  }
  const { discrete, range } = variable.missing;
  if (discrete.length > 0) {
    args.push(
      `na_values = ${rVector(discrete.map((value) => rValue(value, variable.type)))}`
    );
  }
  if (range) {
    const low = range.low === "LO" ? "-Inf" : range.low;
    const high = range.high === "HI" ? "Inf" : range.high;
    args.push(`na_range = c(${low}, ${high})`);
  }
  if (variable.label !== "") {
    args.push(`label = ${quoteR(variable.label)}`);
  }
  return `${target} <- haven::labelled_spss(${args.join(", ")})`;
}

function rHavenSyntax(variables: readonly Variable[], frame: string): string {
  const typeLines = rTypeLines(variables, frame);
  const labelled = variables.filter(
    (variable) =>
      variable.label !== "" ||
      variable.values.length > 0 ||
      variable.missing.discrete.length > 0 ||
      variable.missing.range !== null
  );
  const lines = labelled.map((variable) => rHavenLine(variable, frame));
  const head = `# Codebook syntax for R (haven). Run it after reading your data into \`${frame}\`.\n# labelled_spss() keeps value labels and user-missing codes together, as SPSS does.`;
  const types =
    typeLines.length > 0 ? `# 1. Types\n${typeLines.join("\n")}\n\n` : "";
  return `${head}\n\n${types}# 2. Variable labels, value labels and user-missing codes\n${lines.join("\n")}`;
}

export function rSyntax(
  variables: readonly Variable[],
  style: RStyle,
  frame: string
): string {
  return style === "haven"
    ? rHavenSyntax(variables, frame)
    : rBaseSyntax(variables, frame);
}

/* ------------------------------------------------------------------ the table */

export const CODEBOOK_COLUMNS = [
  "name",
  "label",
  "type",
  "values",
  "missing",
  "notes",
] as const;

export function valuesText(variable: Variable): string {
  return variable.values
    .map((item) => `${item.code} = ${item.label}`)
    .join("; ");
}

export function missingText(variable: Variable): string {
  const { discrete, range } = variable.missing;
  const parts = [
    ...(range ? [`${range.low} to ${range.high}`] : []),
    ...discrete,
  ];
  return parts.join(", ");
}

/** The codebook as rows of text: a header row, then one row per variable. */
export function codebookRows(
  variables: readonly Variable[],
  header: readonly string[],
  typeNames: Record<VarType, string>
): string[][] {
  return [
    [...header],
    ...variables.map((variable) => [
      variable.name,
      variable.label,
      typeNames[variable.type],
      valuesText(variable),
      missingText(variable),
      variable.notes,
    ]),
  ];
}

/* ------------------------------------------------------------------ exports */

const MARKDOWN_SPECIAL = /([|\\])/g;
const CSV_SPECIAL = /[",\r\n]/;

function markdownCell(text: string): string {
  return text.replace(MARKDOWN_SPECIAL, "\\$1").replaceAll("\n", " ");
}

/** A GitHub-flavoured Markdown table. */
export function codebookToMarkdown(rows: readonly string[][]): string {
  if (rows.length === 0) {
    return "";
  }
  const [head, ...body] = rows;
  const line = (cells: readonly string[]) =>
    `| ${cells.map(markdownCell).join(" | ")} |`;
  return [
    line(head),
    `| ${head.map(() => "---").join(" | ")} |`,
    ...body.map(line),
  ].join("\n");
}

function csvCell(text: string): string {
  return CSV_SPECIAL.test(text) ? `"${text.replaceAll('"', '""')}"` : text;
}

/** CSV with newline row breaks; cells with commas, quotes or breaks are quoted. */
export function codebookToCsv(rows: readonly string[][]): string {
  return `${rows.map((row) => row.map(csvCell).join(",")).join("\n")}\n`;
}

const R_FRAME_DEFAULT = "df";

/** A usable R object name for the data frame, or the default `df`. */
export function rFrameName(text: string): string {
  const name = text.trim();
  return name === "" || rNameProblem(name) ? R_FRAME_DEFAULT : name;
}
